import {
  CognitoUserSession,
  CognitoIdToken,
  CognitoAccessToken,
  CognitoRefreshToken,
} from 'amazon-cognito-identity-js';
import { resetCompanyState } from '../../store/companySlice';
import store from '../../store/indexRedux';

// Helper function to check if token is expired.
// `skewSeconds` (default 0 = the original behaviour) treats a token as expired that many
// seconds BEFORE its real `exp`, so a caller can refresh ahead of time instead of sending a
// token that dies in flight.
export const isTokenExpired = (token: string, skewSeconds = 0): boolean => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const { exp } = JSON.parse(jsonPayload);
    const expirationTime = exp * 1000; // exp is in seconds, so multiply by 1000 for milliseconds
    // console.log(expirationTime + ' exp time');
    // console.log(Date.now() + ' NOW');
    return Date.now() >= expirationTime - skewSeconds * 1000;
  } catch (error) {
    console.error('Error parsing token for expiration', error);
    return true; // Consider the token expired if there's an error parsing it
  }
};

// Organization name stored by the login app in the WorkOS session cookie
export const getOrganizationNameFromCookie = (): string | null => {
  const workosTokenStr = document.cookie.split('; ').find((row) => row.startsWith('workos_session='));
  if (!workosTokenStr) return null;
  const raw = workosTokenStr.substring('workos_session='.length);
  for (const value of [raw, decodeURIComponent(raw)]) {
    try {
      return JSON.parse(value)?.OrganizationName ?? null;
    } catch {
      // try the next encoding
    }
  }
  return null;
};

// Retrieve session from cookie set by login-app
export const getSessionFromCookie = (): CognitoUserSession | null => {
  const sessionStr = document.cookie.split('; ').find((row) => row.startsWith('session='));
  const googleTokenStr = document.cookie.split('; ').find((row) => row.startsWith('google_access_token='));
  const workosTokenStr = document.cookie.split('; ').find((row) => row.startsWith('workos_session='));

  // ── WorkOS session ───────────────────────────────────────────────────────
  if (workosTokenStr) {
    const raw = workosTokenStr.substring('workos_session='.length);
    try {
      const parsed = JSON.parse(raw);
      const workosToken = parsed?.AccessToken;
      if (workosToken && !isTokenExpired(workosToken)) {
        return {
          getIdToken: () => ({ getJwtToken: () => workosToken, payload: {} }),
          getAccessToken: () => ({ getJwtToken: () => workosToken }),
          getRefreshToken: () => ({ getToken: () => '' }),
        } as unknown as CognitoUserSession;
      }
    } catch {
      // malformed cookie — fall through to other checks
    }
  }

  if (googleTokenStr) {
    const googleToken = googleTokenStr.split('=')[1];

    // Check for Google token expiration using the expiry time stored in the cookie (not localStorage)
    const expiresInCookie = document.cookie.split('; ').find((row) => row.startsWith('google_token_expires_in='));
    const expiryTimestamp = expiresInCookie ? parseInt(expiresInCookie.split('=')[1], 10) : null;
    // console.log(expiryTimestamp + ' exp time google');
    // console.log(Date.now() + ' exp time now');

    if (expiryTimestamp && Date.now() >= expiryTimestamp) {
      // console.log('Google token expired, logging out.');
      logout(); // Google token expired, log out
      return null;
    }

    // Simulate a Cognito session object for Google login
    return {
      getIdToken: () => ({
        getJwtToken: () => googleToken,
        payload: { email: 'Google User' },
      }),
      getAccessToken: () => ({
        getJwtToken: () => googleToken,
      }),
      getRefreshToken: () => ({
        getToken: () => '',
      }),
    } as unknown as CognitoUserSession;
  }

  if (!sessionStr) {
    console.error('No session found in cookies');
    return null;
  }

  try {
    const sessionData = JSON.parse(sessionStr.split('=')[1]);
    const IdToken = new CognitoIdToken({ IdToken: sessionData.IdToken });
    const AccessToken = new CognitoAccessToken({ AccessToken: sessionData.AccessToken });
    const RefreshToken = new CognitoRefreshToken({ RefreshToken: sessionData.RefreshToken });

    if (isTokenExpired(IdToken.getJwtToken())) {
      // console.log('Cognito token expired, logging out.');
      logout(); // Cognito token expired, log out
      return null;
    }

    return new CognitoUserSession({
      IdToken,
      AccessToken,
      RefreshToken,
    });
  } catch (e) {
    console.error('Failed to parse session from cookie', e);
    return null;
  }
};

const sendLogoutStatusToStatamic = async () => {
  try {
    await fetch('https://yuso.com/api/portal/logout', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        loggedIn: false, // Indicate that the user is logging out
      }),
    });
  } catch (error) {
    console.error('Failed to send logout status to Statamic:', error);
  }
};

export const logout = async () => {
  store.dispatch(resetCompanyState());
  localStorage.removeItem('selectedCompanyId');
  localStorage.removeItem('selectedUserCompany');
    localStorage.clear(); 

  const isLocalhost = window.location.hostname === 'localhost';

  if (isLocalhost) {
    const opts = 'expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=localhost';
    document.cookie = `session=; ${opts}`;
    document.cookie = `google_access_token=; ${opts}`;
    document.cookie = `google_token_expires_in=; ${opts}`;
    document.cookie = `workos_session=; ${opts}`;
    document.cookie = `yuso_logout=1; path=/; SameSite=Lax`;
  } else {
    // Clear on both .yuso.io and .yuso.com so all subdomains are covered
    for (const domain of ['.yuso.io', '.yuso.com']) {
      const opts = `expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain}; SameSite=None; Secure`;
      document.cookie = `session=; ${opts}`;
      document.cookie = `google_access_token=; ${opts}`;
      document.cookie = `google_token_expires_in=; ${opts}`;
      document.cookie = `workos_session=; ${opts}`;
    }
    // Signal other apps to log out
    document.cookie = `yuso_logout=1; path=/; domain=.yuso.com; SameSite=None; Secure`;
  }

  // Clear any session data from localStorage or sessionStorage, if necessary
  localStorage.removeItem('google_token_expires_in');
  sessionStorage.removeItem('referrer');

  try {
    // Notify Statamic about the logout
    await sendLogoutStatusToStatamic();
  } catch (error) {
    console.error('Failed to send logout status to Statamic:', error);
  }

  // Redirect to login page to complete logout process
  window.location.href = import.meta.env.VITE_LOGIN;
};

